// Pure functions used to derive on-time-performance from a live vehicle
// snapshot plus the static GTFS schedule.

export const ON_TIME_THRESHOLD_EARLY_MIN = 1;
export const ON_TIME_THRESHOLD_LATE_MIN = 5;

const ROUTE_MATCH_MAX_DISTANCE_M = 80;
const STATUS_EARLY = 'early';
const STATUS_ON_TIME = 'on-time';
const STATUS_DELAYED = 'delayed';

const BUS_PATTERN = /^\d{4}-\d{2}$/;
const CAR_PATTERN = /^\d{2}-\d{2}$/;

export function classifyVehicleType(name) {
  if (!name) return 'other';
  const trimmed = String(name).trim();
  if (BUS_PATTERN.test(trimmed)) return 'bus';
  if (CAR_PATTERN.test(trimmed)) return 'car';
  return 'other';
}

// Haversine distance between two lat/lng pairs in metres.
export function haversineMeters(a, b) {
  const R = 6371000;
  const toRad = (deg) => (deg * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const x =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(x));
}

// Distance in metres from point p to the segment [a, b], using a flat-earth
// projection (good enough at AMA scales).
function distanceToSegmentMeters(p, a, b) {
  const latRef = (a.lat + b.lat) / 2;
  const mPerDegLat = 111320;
  const mPerDegLng = 111320 * Math.cos((latRef * Math.PI) / 180);
  const ax = a.lng * mPerDegLng;
  const ay = a.lat * mPerDegLat;
  const bx = b.lng * mPerDegLng;
  const by = b.lat * mPerDegLat;
  const px = p.lng * mPerDegLng;
  const py = p.lat * mPerDegLat;
  const dx = bx - ax;
  const dy = by - ay;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) {
    return Math.hypot(px - ax, py - ay);
  }
  const t = Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / lenSq));
  const cx = ax + t * dx;
  const cy = ay + t * dy;
  return Math.hypot(px - cx, py - cy);
}

// Returns the smallest distance in metres from `point` to any segment of the
// given polyline.
function distancePointToShape(point, points) {
  if (!points || points.length === 0) return Infinity;
  if (points.length === 1) return haversineMeters(point, points[0]);
  let best = Infinity;
  for (let i = 0; i < points.length - 1; i += 1) {
    const d = distanceToSegmentMeters(point, points[i], points[i + 1]);
    if (d < best) best = d;
  }
  return best;
}

// Picks the GTFS shape whose polyline is closest to the vehicle's current
// position. Returns null if no shape is within ROUTE_MATCH_MAX_DISTANCE_M.
export function inferShapeForVehicle(vehicle, shapes) {
  if (!vehicle || !Number.isFinite(vehicle.lat) || !Number.isFinite(vehicle.lng)) {
    return null;
  }
  const point = { lat: vehicle.lat, lng: vehicle.lng };
  let bestShape = null;
  let bestDistance = ROUTE_MATCH_MAX_DISTANCE_M;
  for (const shape of shapes) {
    const d = distancePointToShape(point, shape.points);
    if (d < bestDistance) {
      bestDistance = d;
      bestShape = shape;
    }
  }
  return bestShape ? { shape: bestShape, distance: bestDistance } : null;
}

// Resolve the vehicle to a GTFS route via the inferred shape: shapes are
// referenced by trips, trips belong to routes. Multiple trips may share a
// shape; we pick the most common route_id among them.
export function inferRouteForVehicle(vehicle, shapes, trips) {
  const match = inferShapeForVehicle(vehicle, shapes);
  if (!match) return null;
  const candidateTrips = trips.filter((t) => t.shapeId === match.shape.shapeId);
  if (candidateTrips.length === 0) return null;
  const routeCounts = new Map();
  for (const t of candidateTrips) {
    routeCounts.set(t.routeId, (routeCounts.get(t.routeId) || 0) + 1);
  }
  let bestRoute = null;
  let bestCount = -1;
  routeCounts.forEach((count, routeId) => {
    if (count > bestCount) {
      bestCount = count;
      bestRoute = routeId;
    }
  });
  return bestRoute
    ? { routeId: bestRoute, shapeId: match.shape.shapeId, distance: match.distance }
    : null;
}

// Convert GTFS "HH:MM:SS" (which can exceed 24:00:00 for overnight trips) to
// minutes since midnight.
export function gtfsTimeToMinutes(time) {
  if (!time) return null;
  const parts = time.split(':');
  if (parts.length < 2) return null;
  const h = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
  // GTFS times include seconds (e.g. "08:30:45"). Including them as a
  // fraction keeps consecutive close-by stops distinguishable in deltas
  // and sort order; rendering still floors to whole minutes.
  const s = parts.length >= 3 ? parseInt(parts[2], 10) : 0;
  return h * 60 + m + (Number.isFinite(s) ? s / 60 : 0);
}

export function nowMinutesSinceMidnight(date = new Date()) {
  return date.getHours() * 60 + date.getMinutes() + date.getSeconds() / 60;
}

// For a vehicle on a known route, find the next scheduled stop ahead of it
// based on current GPS position and the schedule. Returns { stop, scheduledMinutes,
// distanceM } or null if nothing is upcoming.
export function findNextStop({ vehicle, routeShape, stops, stopTimes, trips, routeId, now = new Date() }) {
  if (!vehicle || !routeShape || !stops || !stopTimes || !trips) return null;

  // Trips active on this shape — pick one whose schedule envelope contains
  // the current time. We don't have calendar service-day filtering here, so
  // we accept any trip; in practice the schedule converges on what's running.
  const tripsForShape = trips.filter((t) => t.shapeId === routeShape.shapeId && t.routeId === routeId);
  if (tripsForShape.length === 0) return null;

  const nowMin = nowMinutesSinceMidnight(now);
  const stopById = new Map(stops.map((s) => [s.stopId, s]));

  let bestCandidate = null;
  let bestDistance = Infinity;

  for (const trip of tripsForShape) {
    const tripStops = stopTimes
      .filter((st) => st.tripId === trip.tripId)
      .sort((a, b) => a.sequence - b.sequence);
    if (tripStops.length === 0) continue;

    for (const st of tripStops) {
      const scheduledMin = gtfsTimeToMinutes(st.arrivalTime || st.departureTime);
      if (scheduledMin === null) continue;
      if (scheduledMin < nowMin - 2) continue; // already passed
      const stop = stopById.get(st.stopId);
      if (!stop) continue;
      const d = haversineMeters({ lat: vehicle.lat, lng: vehicle.lng }, stop);
      if (d < bestDistance) {
        bestDistance = d;
        bestCandidate = { stop, scheduledMinutes: scheduledMin, distanceM: d, tripId: trip.tripId };
      }
    }
  }

  return bestCandidate;
}

// Given the next stop's scheduled time and the vehicle's current position +
// speed, classify whether the vehicle is early / on-time / delayed for that
// stop. Falls back to comparing scheduled vs current time if speed is zero.
export function classifyNextStopOtp({ vehicle, nextStop, now = new Date() }) {
  if (!nextStop) return { status: STATUS_ON_TIME, deltaMinutes: 0 };
  const speedKmh = Math.max(vehicle.speedKmh || vehicle.SpeedKmh || 0, 1);
  const speedMps = (speedKmh * 1000) / 3600;
  const etaSeconds = nextStop.distanceM / speedMps;
  const etaMin = nowMinutesSinceMidnight(now) + etaSeconds / 60;
  const deltaMin = etaMin - nextStop.scheduledMinutes;
  return { status: classifyDelta(deltaMin), deltaMinutes: deltaMin };
}

function classifyDelta(deltaMin) {
  if (deltaMin < -ON_TIME_THRESHOLD_EARLY_MIN) return STATUS_EARLY;
  if (deltaMin > ON_TIME_THRESHOLD_LATE_MIN) return STATUS_DELAYED;
  return STATUS_ON_TIME;
}

// Aggregate per-route OTP counts from a list of vehicle-level classifications.
// `assignments` is an array of { routeId, vehicleName, status }.
export function aggregateRouteOtp(assignments) {
  const byRoute = new Map();
  for (const a of assignments) {
    if (!a.routeId) continue;
    if (!byRoute.has(a.routeId)) {
      byRoute.set(a.routeId, {
        routeId: a.routeId,
        assignedVehicles: new Set(),
        onTime: 0,
        early: 0,
        delayed: 0,
        total: 0
      });
    }
    const entry = byRoute.get(a.routeId);
    if (a.vehicleName) entry.assignedVehicles.add(a.vehicleName);
    entry.total += 1;
    if (a.status === STATUS_EARLY) entry.early += 1;
    else if (a.status === STATUS_DELAYED) entry.delayed += 1;
    else entry.onTime += 1;
  }
  return Array.from(byRoute.values()).map((e) => ({
    routeId: e.routeId,
    assignedVehicles: Array.from(e.assignedVehicles),
    onTimeCount: e.onTime,
    earlyCount: e.early,
    delayedCount: e.delayed,
    totalTrips: e.total,
    otpPercentage: e.total === 0 ? 0 : Math.round((e.onTime / e.total) * 1000) / 10
  }));
}

export const Statuses = Object.freeze({
  Early: STATUS_EARLY,
  OnTime: STATUS_ON_TIME,
  Delayed: STATUS_DELAYED
});
